import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
} from "@mui/material";
import { SketchPicker } from "react-color";
import api from "../../api/api"; // Импорт API клиента
import { ThemeContext } from "../context/ThemeContext"; // Импорт контекста темы
import RentalBusinessSettings from "./BusinessSettings"; // Импорт компонента настроек rental бизнеса
import ChannelSettings from "./ChannelSettings"; // Настройки каналов связи (боты)

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function Settings() {
  const [tabValue, setTabValue] = useState(0);
  const { theme, setTheme } = useContext(ThemeContext); // Используем контекст для изменения темы
  
  // Состояния для кастомных цветов
  const [customColors, setCustomColors] = useState({
    navbarColor: theme.navbarColor || "#1976d2",
    sidebarColor: theme.sidebarColor || "#f5f5f5",
  });
  const [showNavbarPicker, setShowNavbarPicker] = useState(false);
  const [showSidebarPicker, setShowSidebarPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [loadError, setLoadError] = useState("");
  const mounted = useRef(false);
  const operationPending = useRef(false);


  // Загружаем индивидуальную тему пользователя при инициализации
  useEffect(() => {
    mounted.current = true;
    const loadUserTheme = async () => {
      try {
        // Загружаем тему пользователя с сервера
        const response = await api.get("/user-theme/theme", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!mounted.current) return;

        const userTheme = {
          navbarColor: response.data.navbar_color,
          sidebarColor: response.data.sidebar_color,
        };

        setCustomColors(userTheme);
        setTheme({
          ...theme,
          ...userTheme,
        });
      } catch (error) {
        if (mounted.current) setLoadError("Не удалось загрузить оформление");
      } finally {
        if (mounted.current) setLoading(false);
      }
    };

    loadUserTheme();
    return () => { mounted.current = false; };
  }, []);

  // Функция для сохранения кастомных цветов
  const handleSaveCustomColors = async () => {
    if (loading || operationPending.current) return;
    operationPending.current = true;
    setPending(true);
    try {
      console.log("🎨 Сохраняем цвета темы:", customColors);

      // Отправляем запрос на сервер для сохранения в базе данных
      const response = await api.put(
        "/user-theme/theme",
        {
          navbar_color: customColors.navbarColor,
          sidebar_color: customColors.sidebarColor,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!mounted.current) return;

      // Обновляем тему локально
      setTheme({
        ...theme,
        navbarColor: customColors.navbarColor,
        sidebarColor: customColors.sidebarColor,
      });

      // Удаляем локальное хранение, т.к. теперь данные в базе
      localStorage.removeItem("customTheme");

      alert("Настройки интерфейса сохранены!");
    } catch (error) {
      if (!mounted.current) return;
      console.error("Ошибка сохранения цветов:", error);
      if (error.response) {
        console.error("Ответ от сервера:", error.response.data);
      }
      alert("Ошибка при сохранении настроек.");
    } finally {
      operationPending.current = false;
      if (mounted.current) setPending(false);
    }
  };

  // Функция для сброса цветов к дефолтным
  const handleResetColors = async () => {
    if (loading || operationPending.current) return;
    operationPending.current = true;
    setPending(true);
    try {
      console.log("🔄 Сбрасываем цвета темы к дефолтным");

      // Отправляем запрос на сервер для сброса
      const response = await api.post(
        "/user-theme/theme/reset",
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!mounted.current) return;

      const defaultColors = {
        navbarColor: response.data.navbar_color,
        sidebarColor: response.data.sidebar_color,
      };

      setCustomColors(defaultColors);
      setTheme({
        ...theme,
        ...defaultColors,
      });

      // Удаляем локальное хранение
      localStorage.removeItem("customTheme");

      alert("Цвета сброшены к стандартным значениям!");
    } catch (error) {
      if (!mounted.current) return;
      console.error("⚠️ Ошибка сброса цветов:", error);

      // Fallback: сбрасываем локально
      const defaultColors = {
        navbarColor: "#1976d2",
        sidebarColor: "#f5f5f5",
      };
      setCustomColors(defaultColors);
      setTheme({
        ...theme,
        ...defaultColors,
      });
      localStorage.removeItem("customTheme");

      alert("Цвета сброшены локально (ошибка подключения к серверу)");
    } finally {
      operationPending.current = false;
      if (mounted.current) setPending(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Настройки
      </Typography>

      {loadError && <Alert severity="error">{loadError}</Alert>}
      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Интерфейс" />
          <Tab label="Настройки бизнеса" />
          <Tab label="Каналы связи" />
        </Tabs>
        
        {/* Вкладка настройки интерфейса */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Настройка интерфейса
          </Typography>

          <Grid container spacing={3}>
            {/* Настройка цвета верхней панели */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Цвет верхней панели
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      height: 80,
                      backgroundColor: customColors.navbarColor,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      mb: 2,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                    component="button" type="button" aria-label="Выбрать цвет верхней панели" disabled={loading || pending}
                    onClick={() => setShowNavbarPicker(!showNavbarPicker)}
                  >
                    Нажмите для выбора цвета
                  </Box>
                  
                  {showNavbarPicker && (
                    <Box sx={{ position: "absolute", zIndex: 1000, mt: 1 }}>
                      <SketchPicker
                        color={customColors.navbarColor}
                        onChangeComplete={(color) => {
                          setCustomColors((prev) => ({
                            ...prev,
                            navbarColor: color.hex,
                          }));
                        }}
                      />
                      <Button
                        onClick={() => setShowNavbarPicker(false)}
                        sx={{ mt: 1 }}
                        fullWidth
                        variant="outlined"
                      >
                        Закрыть
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Настройка цвета боковой панели */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Цвет боковой панели
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      height: 80,
                      backgroundColor: customColors.sidebarColor,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      mb: 2,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#333",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                    component="button" type="button" aria-label="Выбрать цвет боковой панели" disabled={loading || pending}
                    onClick={() => setShowSidebarPicker(!showSidebarPicker)}
                  >
                    Нажмите для выбора цвета
                  </Box>
                  
                  {showSidebarPicker && (
                    <Box sx={{ position: "absolute", zIndex: 1000, mt: 1 }}>
                      <SketchPicker
                        color={customColors.sidebarColor}
                        onChangeComplete={(color) => {
                          setCustomColors((prev) => ({
                            ...prev,
                            sidebarColor: color.hex,
                          }));
                        }}
                      />
                      <Button
                        onClick={() => setShowSidebarPicker(false)}
                        sx={{ mt: 1 }}
                        fullWidth
                        variant="outlined"
                      >
                        Закрыть
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveCustomColors}
              disabled={loading || pending}
              fullWidth
            >
              Сохранить настройки
            </Button>
            <Button variant="outlined" onClick={handleResetColors} disabled={loading || pending} fullWidth>
              Сбросить к стандартным
            </Button>
          </Box>
        </TabPanel>
        
        {/* Вкладка настроек бизнеса */}
        <TabPanel value={tabValue} index={1}>
          <RentalBusinessSettings />
        </TabPanel>

        {/* Вкладка каналов связи */}
        <TabPanel value={tabValue} index={2}>
          <ChannelSettings />
        </TabPanel>
      </Paper>
    </Container>
  );
}

export default Settings;
